import {
  boundedRecords,
  limits,
  type DiagnosticRecord,
  type DiagnosticStore,
} from './LocalDiagnostics';

/** Separate database: never opens, migrates, clears or enumerates project storage. */
export class IndexedDbDiagnosticStore implements DiagnosticStore {
  constructor(private readonly factory: () => IDBFactory = () => indexedDB) {}
  private transact(
    update: (records: unknown) => DiagnosticRecord[],
  ): Promise<DiagnosticRecord[]> {
    return new Promise((resolve, reject) => {
      let database: IDBDatabase | undefined;
      let transaction: IDBTransaction | undefined;
      let settled = false;
      const finish = (error?: unknown, result: DiagnosticRecord[] = []) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        database?.close();
        if (error) reject(new Error('Diagnostic storage unavailable'));
        else resolve(result);
      };
      const timeout = setTimeout(() => {
        try {
          transaction?.abort();
        } catch {
          /* Already complete. */
        }
        finish(true);
      }, 2000);
      try {
        const open = this.factory().open('digitiles-support-diagnostics-v1', 1);
        open.onupgradeneeded = () => open.result.createObjectStore('history');
        open.onerror = () => finish(true);
        open.onblocked = () => finish(true);
        open.onsuccess = () => {
          database = open.result;
          if (settled) {
            database.close();
            return;
          }
          database.onversionchange = () => database?.close();
          try {
            transaction = database.transaction('history', 'readwrite');
            const store = transaction.objectStore('history');
            const read = store.get('records');
            let result: DiagnosticRecord[] = [];
            read.onsuccess = () => {
              try {
                result = update(read.result);
                if (
                  new TextEncoder().encode(JSON.stringify(result)).length > limits.bytes
                )
                  throw Error();
                store.put(result, 'records');
              } catch {
                try {
                  transaction?.abort();
                } catch {
                  /* Already complete. */
                }
                finish(true);
              }
            };
            transaction.oncomplete = () => finish(undefined, result);
            transaction.onerror = transaction.onabort = () => finish(true);
          } catch {
            finish(true);
          }
        };
      } catch {
        finish(true);
      }
    });
  }
  append(records: DiagnosticRecord[], now: number) {
    return this.transact((old) =>
      boundedRecords([...boundedRecords(old, now), ...records], now),
    );
  }
  read(now: number) {
    return this.transact((old) => boundedRecords(old, now));
  }
  async clear() {
    await this.transact(() => []);
  }
}
